import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Info, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { useAuth } from "../contexts/AuthContext";
import { fetchTiers, fetchMyBilling, startCheckout, openBillingPortal } from "../lib/billing";
import { track } from "../lib/analytics";
import StartBuildingButton from "../components/StartBuildingButton";
import EmailSignInDialog from "../components/EmailSignInDialog";

const formatPrice = (cad, currency) =>
  cad === 0 ? "Free" : `$${cad} ${currency || "CAD"}`;

export default function Pricing() {
  const { user, signIn } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [billing, setBilling] = useState(null);
  const [period, setPeriod] = useState("yearly"); // default to yearly (better value)
  const [loading, setLoading] = useState(true);
  const [checkoutTier, setCheckoutTier] = useState(null); // tier_id currently redirecting

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tiers, mine] = await Promise.all([
          fetchTiers(),
          user ? fetchMyBilling().catch(() => null) : Promise.resolve(null),
        ]);
        if (!cancelled) {
          setData(tiers);
          setBilling(mine);
        }
      } catch (e) {
        if (!cancelled) toast.error("Could not load pricing — try again in a sec.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleSubscribe = async (tier) => {
    if (tier.is_free) {
      navigate("/");
      return;
    }
    if (!user) {
      // Stash the desired tier so the ResumeCheckout effect can fire
      // it back up the moment they finish signing in (Google OR magic
      // link). localStorage (not sessionStorage) so it survives the
      // "click email link → new tab" hop teachers actually do.
      try {
        localStorage.setItem(
          "atomicedu.pendingCheckout",
          JSON.stringify({ tier_id: tier.id, billing_period: period }),
        );
      } catch {
        /* fine if storage is full */
      }
      signIn();
      return;
    }
    setCheckoutTier(tier.id);
    try {
      // Analytics — fire before redirect so the funnel is intact even
      // if Stripe is slow or the user bails on the Stripe page.
      track("subscription_checkout_started", {
        tier_id: tier.id,
        tier_name: tier.name || "",
        billing_period: period,
      });
      const { url } = await startCheckout({ tier_id: tier.id, billing_period: period });
      window.location.href = url;
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Could not start checkout";
      toast.error("Couldn't start checkout", { description: String(detail) });
      setCheckoutTier(null);
    }
  };

  const handleManage = async () => {
    try {
      const { url } = await openBillingPortal();
      window.location.href = url;
    } catch (e) {
      toast.error("Couldn't open billing portal — try again in a sec.");
    }
  };

  const currentTierId = billing?.is_active ? billing.tier_id : "free";

  return (
    <div className="min-h-screen bg-[#f4f5f7] text-[#1c2d3d]" data-testid="pricing-page">
      <header className="bg-[#1c2d3d] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between gap-2">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-extrabold tracking-tight hover:opacity-80 whitespace-nowrap shrink-0 text-base sm:text-lg"
            data-testid="pricing-home-link"
          >
            <img
              src="/brand/atomicedu-logo-mark.png"
              alt="Atomic Edu"
              className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0"
            />
            Atomic Edu
          </Link>
          <div className="flex items-center gap-2 shrink-0">
            <span className="hidden md:inline-flex">
              <StartBuildingButton />
            </span>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-white/85 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-14">
        {/* Phase 145 — Early Bird banner. Slate-navy on mint so it
            reads like a deadline announcement without introducing new
            colours. */}
        <div
          data-testid="early-bird-banner"
          className="max-w-3xl mx-auto mb-8 rounded-xl border border-[#96ded1] bg-[#96ded1]/25 px-5 py-3 text-center"
        >
          <p className="text-sm font-extrabold text-[#1c2d3d] leading-snug">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#1c2d3d] text-white text-[10px] font-black uppercase tracking-[0.15em] mr-2 align-middle">
              <Sparkles className="h-3 w-3" /> Early bird
            </span>
            Lock in your rate. Early bird pricing ends October 31.
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="early-bird-info-trigger"
                  aria-label="What's included in Early Bird"
                  className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full text-[#1c2d3d]/70 hover:text-[#1c2d3d] hover:bg-[#1c2d3d]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1c2d3d]/40 align-middle transition-colors"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                data-testid="early-bird-info-popover"
                align="center"
                sideOffset={8}
                className="w-80 border-[#96ded1] bg-white text-left"
              >
                <p className="text-xs font-black uppercase tracking-[0.15em] text-[#1c2d3d]/70 mb-3">
                  What's included in Early Bird
                </p>
                <ul className="space-y-2.5 text-sm text-[#1c2d3d] font-medium leading-snug">
                  <li data-testid="early-bird-info-bullet-rate" className="flex gap-2">
                    <Check className="h-4 w-4 text-[#96ded1] mt-0.5 shrink-0" />
                    <span><strong className="font-black">Your rate.</strong> The price you sign up at is the price you keep.</span>
                  </li>
                  <li data-testid="early-bird-info-bullet-features" className="flex gap-2">
                    <Check className="h-4 w-4 text-[#96ded1] mt-0.5 shrink-0" />
                    <span><strong className="font-black">Your features.</strong> Whatever's included in your plan today stays included.</span>
                  </li>
                  <li data-testid="early-bird-info-bullet-upgrades" className="flex gap-2">
                    <Check className="h-4 w-4 text-[#96ded1] mt-0.5 shrink-0" />
                    <span><strong className="font-black">Plan upgrades.</strong> Move up later and your early-bird pricing comes with you to the new plan.</span>
                  </li>
                  <li data-testid="early-bird-info-bullet-reset" className="flex gap-2">
                    <Check className="h-4 w-4 text-[#96ded1] mt-0.5 shrink-0" />
                    <span><strong className="font-black">One reset rule.</strong> The rate only resets if you cancel and resubscribe.</span>
                  </li>
                </ul>
              </PopoverContent>
            </Popover>
          </p>
        </div>

        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#96ded1]/30 text-[#1c2d3d] text-xs font-extrabold uppercase tracking-[0.18em] mb-4">
            <Sparkles className="h-3.5 w-3.5" /> Pick your plan
          </div>
          <h1 className="font-black text-4xl sm:text-5xl lg:text-6xl text-[#1c2d3d] tracking-tight leading-tight">
            Try any plan free<br />
            <span className="text-[#4a6984]">for 7 days.</span>
          </h1>
          <p className="mt-5 text-lg text-[#4a6984] font-medium">
            Full access. Cancel anytime from your billing portal.
            <br className="hidden sm:inline" />
            {" "}You won't be charged until your trial ends. No school-board paperwork.
          </p>

          {/* Period toggle */}
          <div className="mt-8 inline-flex flex-wrap items-center bg-white rounded-full border border-[#4a6984]/15 p-1 shadow-sm gap-1 max-w-full">
            <button
              data-testid="period-monthly"
              onClick={() => setPeriod("monthly")}
              className={`px-5 py-2 rounded-full text-sm font-extrabold transition ${
                period === "monthly"
                  ? "bg-[#1c2d3d] text-white"
                  : "text-[#4a6984] hover:text-[#1c2d3d]"
              }`}
            >
              Monthly
            </button>
            <button
              data-testid="period-yearly"
              onClick={() => setPeriod("yearly")}
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-extrabold transition ${
                period === "yearly"
                  ? "bg-[#1c2d3d] text-white"
                  : "text-[#4a6984] hover:text-[#1c2d3d]"
              }`}
            >
              <span>Yearly</span>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                  period === "yearly"
                    ? "bg-[#96ded1] text-[#1c2d3d]"
                    : "bg-[#96ded1]/40 text-[#1c2d3d]"
                }`}
              >
                Save 2 months
              </span>
            </button>
          </div>
        </div>

        {/* Current plan banner */}
        {user && billing?.is_active && billing.tier_id !== "free" && (
          <div
            className="max-w-3xl mx-auto mb-10 rounded-xl border border-[#96ded1] bg-[#96ded1]/15 px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            data-testid="current-plan-banner"
          >
            <div className="text-sm text-[#1c2d3d]">
              <span className="font-extrabold">You're on the {billing.tier_name} plan</span>
              {billing.status === "trialing" && billing.trial_end ? (
                <span className="ml-2 text-[#4a6984]">
                  Trial ends {new Date(billing.trial_end).toLocaleDateString()}
                </span>
              ) : billing.current_period_end ? (
                <span className="ml-2 text-[#4a6984]">
                  Renews {new Date(billing.current_period_end).toLocaleDateString()}
                </span>
              ) : null}
            </div>
            <Button
              data-testid="manage-billing-btn"
              onClick={handleManage}
              className="bg-[#1c2d3d] text-white hover:bg-[#1c2d3d]/90 h-9 px-4 rounded-md text-sm font-bold"
            >
              Manage billing
            </Button>
          </div>
        )}

        {/* Tier grid */}
        {loading ? (
          <div className="text-center py-20 text-[#4a6984]">
            <Loader2 className="h-6 w-6 mx-auto animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {data?.tiers?.map((tier) => {
              const price = period === "yearly" ? tier.price_yearly_cad : tier.price_monthly_cad;
              const isCurrent = currentTierId === tier.id;
              const isLoading = checkoutTier === tier.id;
              const isComingSoon = !!tier.coming_soon;
              return (
                <div
                  key={tier.id}
                  data-testid={`tier-card-${tier.id}`}
                  className={`relative rounded-2xl border bg-white p-6 flex flex-col ${
                    isComingSoon
                      ? "border-[#4a6984]/15 shadow-sm opacity-95"
                      : tier.highlight
                      ? "border-[#96ded1] shadow-xl ring-2 ring-[#96ded1]/40"
                      : "border-[#4a6984]/15 shadow-sm"
                  }`}
                >
                  {isComingSoon ? (
                    <span
                      data-testid={`coming-soon-${tier.id}`}
                      className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center px-3 py-1 rounded-full bg-[#4a6984] text-white text-[10px] font-black uppercase tracking-widest"
                    >
                      {tier.coming_soon_label || "Coming soon"}
                    </span>
                  ) : tier.highlight ? (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center px-3 py-1 rounded-full bg-[#1c2d3d] text-white text-[10px] font-black uppercase tracking-widest">
                      Most popular
                    </span>
                  ) : null}
                  <h3 className="font-black text-2xl text-[#1c2d3d]">{tier.name}</h3>
                  <p className="mt-1 text-sm text-[#4a6984]/85 min-h-[40px]">{tier.tagline}</p>
                  <div className="mt-4">
                    {tier.is_free ? (
                      <div className="font-black text-4xl text-[#1c2d3d]">Free</div>
                    ) : isComingSoon ? (
                      <>
                        <div className="flex items-baseline gap-1.5">
                          <span className="font-black text-4xl text-[#4a6984]">
                            ${price}
                          </span>
                          <span className="text-sm font-bold text-[#4a6984]/80">
                            {data.currency} / {period === "yearly" ? "year" : "month"}
                          </span>
                        </div>
                        {/* Phase 145 — Pro+ keeps its launch sub-line
                            instead of the early-bird line (spec). */}
                        <p className="mt-1 text-xs text-[#4a6984]/70 font-medium">
                          {tier.coming_soon_subline || "Launches late summer 2026 · ready for the new school year"}
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-1.5">
                          <span className="font-black text-4xl text-[#1c2d3d]">
                            ${price}
                          </span>
                          <span className="text-sm font-bold text-[#4a6984]/80">
                            {data.currency} / {period === "yearly" ? "year" : "month"}
                          </span>
                        </div>
                        {/* Phase 145 — Early-bird line on every paid card. */}
                        <p
                          data-testid={`tier-early-bird-line-${tier.id}`}
                          className="mt-2 text-[11px] leading-snug text-[#1c2d3d]/80 font-semibold italic"
                        >
                          Early bird rate — ends October 31. Lock it in now and keep it as long as you stay subscribed.
                        </p>
                        <p className="mt-1 text-xs text-[#4a6984]/70 font-medium">
                          {tier.trial_days}-day free trial · cancel anytime
                        </p>
                      </>
                    )}
                  </div>

                  <ul className="mt-5 space-y-2.5 text-sm text-[#4a6984] flex-1">
                    {tier.features.map((f, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2"
                        data-testid={`tier-feature-${tier.id}-${i}`}
                      >
                        <Check className="h-4 w-4 text-[#96ded1] mt-0.5 shrink-0" />
                        <span className="leading-snug">{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    data-testid={`subscribe-${tier.id}`}
                    disabled={isCurrent || isLoading || isComingSoon}
                    onClick={() => handleSubscribe(tier)}
                    className={`mt-6 w-full h-11 rounded-lg font-extrabold text-sm ${
                      isComingSoon
                        ? "bg-[#f4f5f7] border border-[#4a6984]/15 text-[#4a6984]/80 cursor-not-allowed"
                        : tier.highlight
                        ? "bg-[#1c2d3d] text-white hover:bg-[#1c2d3d]/90"
                        : tier.is_free
                        ? "bg-white border border-[#4a6984]/25 text-[#1c2d3d] hover:bg-[#f4f5f7]"
                        : "bg-[#4a6984] text-white hover:bg-[#4a6984]/90"
                    }`}
                  >
                    {isComingSoon ? (
                      tier.coming_soon_label || "Coming soon"
                    ) : isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Opening Stripe…
                      </>
                    ) : isCurrent ? (
                      "Your current plan"
                    ) : tier.is_free ? (
                      "Start free"
                    ) : !user ? (
                      "Continue with Google"
                    ) : (
                      `Start ${tier.trial_days}-day free trial`
                    )}
                  </Button>
                  {/* Signed-out paid tiers get a secondary passwordless
                      sign-in option so teachers without Google accounts
                      (or who prefer email) can still start a trial.
                      Mirrors the header CTA pair. */}
                  {!user && !tier.is_free && !isComingSoon && (
                    <div
                      data-testid={`subscribe-email-row-${tier.id}`}
                      className="mt-2 text-center"
                    >
                      <EmailSignInDialog
                        triggerLabel="Or sign in with email"
                        triggerClassName="h-9 px-3 text-xs font-bold inline-flex items-center gap-1.5 text-[#4a6984] hover:text-[#1c2d3d] bg-transparent hover:bg-[#f4f5f7] border-0"
                        onBeforeOpen={() => {
                          // Stash the chosen tier so ResumeCheckout can
                          // fire it once the magic link completes —
                          // same key the Google flow uses.
                          try {
                            localStorage.setItem(
                              "atomicedu.pendingCheckout",
                              JSON.stringify({
                                tier_id: tier.id,
                                billing_period: period,
                              }),
                            );
                          } catch {
                            /* fine */
                          }
                          track("subscription_email_signin_started", {
                            tier_id: tier.id,
                            tier_name: tier.name || "",
                            billing_period: period,
                          });
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Phase 145 — Early-bird grandfather note (replaces the
            previous Standard-only Report Card Builder grandfather
            block — the early-bird promise covers both: your rate AND
            features promised at signup carry forward as long as the
            subscription stays continuous). */}
        <div
          data-testid="early-subscriber-note"
          className="max-w-3xl mx-auto mt-12 rounded-xl border border-[#96ded1]/60 bg-[#96ded1]/12 px-6 py-5 text-[#1c2d3d]"
        >
          <p className="text-sm leading-relaxed">
            <strong>Early bird pricing locks in for good.</strong> Subscribe
            before October&nbsp;31 and you keep your early bird rate for as
            long as you stay subscribed. Decide to move up to a higher plan
            later? Your early bird pricing comes with you to the new plan.
            The rate only resets if you cancel and resubscribe.
          </p>
        </div>

        {/* Footnote */}
        <div className="max-w-3xl mx-auto mt-8 text-center text-xs text-[#4a6984]/70 font-medium">
          Prices in Canadian dollars. Taxes calculated at checkout where applicable.
          Manage or cancel your subscription anytime through the Stripe billing portal.
          Questions? <a href="mailto:info@atomicedu.ca" className="underline hover:text-[#1c2d3d]">info@atomicedu.ca</a>
        </div>
      </main>
    </div>
  );
}
